<template>
    <div class="home">
        <Header class="home-header"></Header>

        <div class="main">
            <section class="hero-section">
                <img class="hero-image" :src="$gm('1-1.png')" alt="expo banner" />
            </section>

            <section class="intro-wrap" :style="introWrapStyle">
                <div class="section-shell">
                    <section class="action-section">
                        <div
                            class="action-card"
                            v-for="card in actionCards"
                            :key="card.title"
                            @click="handleAction(card)"
                        >
                            <div class="action-title">{{ card.title }}</div>
                            <div class="action-button">{{ card.button }}</div>
                        </div>
                    </section>

                    <section class="content-section intro-section">
                        <div class="section-title">
                            {{ $t("home.expoProfileTitle") }}
                        </div>
                        <div class="intro-panel">
                            <div class="intro-copy" :class="{ en: isEnglish }">
                                {{ $t("home.expoProfileText") }}
                            </div>
                            <div class="intro-media">
                                <video
                                    class="intro-video"
                                    :src="introVideo"
                                    controls
                                    preload="metadata"
                                ></video>
                            </div>
                        </div>
                    </section>
                </div>
            </section>

            <section class="data-section" :style="dataWrapStyle">
                <div class="section-shell">
                    <div class="section-title section-title-light">
                        {{ dataTitle }}
                    </div>
                    <div class="stats-grid">
                        <div
                            class="stat-card"
                            v-for="item in statsList"
                            :key="item.label"
                        >
                            <div class="stat-value">{{ item.value }}</div>
                            <div class="stat-meta">
                                <div class="stat-label">{{ item.label }}</div>
                                <div class="stat-unit">{{ item.unit }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section class="content-section news-section">
                <div class="section-shell">
                    <div class="section-title">
                        {{ workTitle }}
                    </div>
                    <div class="news-panel">
                        <div class="news-list">
                            <div
                                class="news-item"
                                v-for="item in featuredNews"
                                :key="item.timer + item.title"
                                @click="openLink(item.link)"
                            >
                                <div class="news-date">{{ formatDate(item.timer) }}</div>
                                <div class="news-text">{{ item.title }}</div>
                            </div>
                        </div>
                        <div class="news-cover" @click="openLink(newsCoverLink)">
                            <img :src="newsImage" alt="work update" />
                        </div>
                    </div>
                </div>
            </section>

            <section class="gallery-section">
                <div class="section-shell">
                    <div class="section-title section-title-light">
                        {{ galleryTitle }}
                    </div>
                    <div class="gallery-marquee">
                        <div
                            class="gallery-item"
                            v-for="(photo, index) in duplicatedGalleryPhotos"
                            :key="photo.img + '-' + index"
                        >
                            <img :src="photo.img" :alt="photo.alt" />
                        </div>
                    </div>
                    <div class="section-cta" @click="openGalleryMore">
                        {{ moreText }}
                    </div>
                </div>
            </section>

            <section class="directory-section">
                <div class="section-shell">
                    <div class="section-title">{{ directoryTitle }}</div>
                    <div class="section-note">{{ directoryNote }}</div>
                    <div class="logo-grid">
                        <div
                            class="logo-card"
                            v-for="item in visibleLogoList"
                            :key="item.img || item.name"
                        >
                            <img :src="item.img" :alt="item.name || 'logo'" />
                        </div>
                    </div>
                    <div
                        v-if="shopLogoList.length > logoPreviewCount"
                        class="directory-more"
                        @click="toggleDirectoryExpand"
                    >
                        <div class="directory-more-text">{{ directoryMoreText }}</div>
                        <div class="directory-more-icon"></div>
                    </div>
                </div>
            </section>

            <section class="contact-strip">
                <div class="section-shell contact-shell">
                    <div class="contact-qr">
                        <img :src="$gm('gzhqr.jpg')" alt="wechat qrcode" />
                        <div class="contact-caption">{{ $t("home.followWechat") }}</div>
                    </div>
                    <div class="contact-info">
                        <div class="contact-line">{{ contactText }}</div>
                        <div class="contact-line">{{ recordText }}</div>
                    </div>
                    <div class="contact-info contact-address">
                        <div class="contact-line">{{ addressText }}</div>
                        <div class="contact-line">&nbsp;</div>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import Header from "@/components/header.vue";
import { firmsList, homeNewList } from "@/utils/map";
import { gm } from "@/utils/gm";

export default {
    components: {
        Header,
    },
    data() {
        return {
            navList: [],
            shopLogoList: [],
            isDirectoryExpanded: false,
            logoPreviewCount: 40,
            brochureFile: (process.env.BASE_URL || "./") + "file/manual.pdf",
            introVideo: require("@/assets/file/video.mp4"),
            newsImage: gm("2023-03.jpg"),
        };
    },
    created() {
        this.shopLogoList = firmsList.filter((item) => item.img);
        this.navList = [...homeNewList].sort(
            (a, b) => new Date(b.timer) - new Date(a.timer)
        );
    },
    mounted() {
        this.toggleHomeResponsiveLayout(true);
    },
    beforeDestroy() {
        this.toggleHomeResponsiveLayout(false);
    },
    computed: {
        isEnglish() {
            return this.$i18n.locale === "en";
        },
        actionCards() {
            return [
                {
                    title: this.isEnglish ? "Exhibitor Registration" : "展商报名",
                    button: this.isEnglish ? "Click to register" : "点击报名",
                    type: "register",
                    value: 1,
                },
                {
                    title: this.isEnglish ? "Visitor Registration" : "观众登记",
                    button: this.isEnglish ? "Click to register" : "点击登记",
                    type: "register",
                    value: 2,
                },
                {
                    title: this.isEnglish ? "Concurrent Events" : "本届亮点",
                    button: this.isEnglish ? "View more" : "查看更多",
                    type: "route",
                    value: "/highlights",
                },
                {
                    title: this.isEnglish ? "Download Center" : "下载中心",
                    button: this.isEnglish ? "Click to open" : "点击下载",
                    type: "download",
                },
                {
                    title: this.isEnglish ? "Live Room" : "直播间",
                    button: this.isEnglish ? "Click to watch" : "点击观看",
                    type: "live",
                },
            ];
        },
        statsList() {
            return this.isEnglish
                ? [
                      { value: "2", label: "Halls", unit: "" },
                      { value: "1200", label: "Standard Booths", unit: "" },
                      { value: "2.4", label: "Planned Scale", unit: "10k sq.m." },
                      { value: "4", label: "Expected Visitors", unit: "10k people" },
                  ]
                : [
                      { value: "2", label: "展馆", unit: "个" },
                      { value: "1200", label: "标准展位", unit: "个" },
                      { value: "2.4", label: "预计展出规模", unit: "万平方米" },
                      { value: "4", label: "预计观众", unit: "万人" },
                  ];
        },
        dataTitle() {
            return this.isEnglish ? "EXPO DATA" : "本届博览会大数据";
        },
        workTitle() {
            return this.isEnglish ? "WORK UPDATES" : "工作动态";
        },
        galleryTitle() {
            return this.isEnglish ? "PREVIOUS PHOTOS" : "往届照片";
        },
        directoryTitle() {
            return this.isEnglish ? "2025 EXHIBITOR DIRECTORY" : "2025展商名录";
        },
        directoryNote() {
            return this.isEnglish
                ? "Exhibitors are displayed in random order"
                : "展商顺序随机排列";
        },
        directoryMoreText() {
            if (this.isDirectoryExpanded) {
                return this.isEnglish ? "Collapse" : "收起";
            }
            return this.moreText;
        },
        moreText() {
            return this.isEnglish ? "View More" : "查看更多";
        },
        featuredNews() {
            return this.navList.slice(0, 5);
        },
        newsCoverLink() {
            return this.featuredNews[0] ? this.featuredNews[0].link : "";
        },
        liveLink() {
            const item = this.navList.find((entry) =>
                /直播|live/i.test(entry.title || "")
            );
            return item ? item.link : "";
        },
        galleryPhotos() {
            return [
                {
                    img: gm("微信图片_20260307141220_432_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 1" : "往届照片1",
                },
                {
                    img: gm("微信图片_20260307141221_433_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 2" : "往届照片2",
                },
                {
                    img: gm("微信图片_20260307141222_434_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 3" : "往届照片3",
                },
                {
                    img: gm("微信图片_20260307141224_435_14.png"),
                    alt: this.isEnglish ? "Expo photo 4" : "往届照片4",
                },
                {
                    img: gm("微信图片_20260307141225_436_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 5" : "往届照片5",
                },
                {
                    img: gm("微信图片_20260307141226_437_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 6" : "往届照片6",
                },
                {
                    img: gm("微信图片_20260307141227_438_14.png"),
                    alt: this.isEnglish ? "Expo photo 7" : "往届照片7",
                },
                {
                    img: gm("微信图片_20260307141228_439_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 8" : "往届照片8",
                },
                {
                    img: gm("微信图片_20260307143109_441_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 9" : "往届照片9",
                },
                {
                    img: gm("微信图片_20260307143110_442_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 10" : "往届照片10",
                },
                {
                    img: gm("微信图片_20260307143112_443_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 11" : "往届照片11",
                },
                {
                    img: gm("微信图片_20260307143114_444_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 12" : "往届照片12",
                },
                {
                    img: gm("微信图片_20260307143115_445_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 13" : "往届照片13",
                },
                {
                    img: gm("微信图片_20260307143116_446_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 14" : "往届照片14",
                },
                {
                    img: gm("微信图片_20260307143118_447_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 15" : "往届照片15",
                },
            ];
        },
        duplicatedGalleryPhotos() {
            return [...this.galleryPhotos, ...this.galleryPhotos];
        },
        visibleLogoList() {
            if (this.isDirectoryExpanded) {
                return this.shopLogoList;
            }
            return this.shopLogoList.slice(0, this.logoPreviewCount);
        },
        contactText() {
            return this.isEnglish
                ? "Contact: Mr. Chen 15850576018   Ms. Zhou 15295583680"
                : "联系方式：陈先生 15850576018   周女士 15295583680";
        },
        recordText() {
            return this.isEnglish
                ? "Record No.: Su ICP No. 05085572-16"
                : "2026备案号：苏ICP备05085572号-16";
        },
        addressText() {
            return this.isEnglish
                ? "Venue: Nanjing International Expo Center, 300 Jiangdong Middle Road, Jianye District, Nanjing"
                : "展会地址：南京国际博览中心（南京市建邺区江东中路300号）";
        },
        introWrapStyle() {
            return {
                backgroundImage: `url(${require("@/assets/image/home-bg-1.png")})`,
            };
        },
        dataWrapStyle() {
            return {
                backgroundImage: `url(${require("@/assets/image/home-bg-2.png")})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center center",
                backgroundSize: "cover",
            };
        },
    },
    methods: {
        toggleHomeResponsiveLayout(enabled) {
            const appRoot = document.getElementById("app");

            if (appRoot) {
                appRoot.classList[enabled ? "add" : "remove"](
                    "home-responsive-layout"
                );
            }
        },
        register(type) {
            if (type === 1) {
                this.goRoute("/contactUs");
                return;
            }
            if (type === 2) {
                window.open("https://z.zcyit.cn/green/#/?regid=1191", "_blank");
                return;
            }
            this.$router.push("/register?type=" + type);
        },
        toggleDirectoryExpand() {
            this.isDirectoryExpanded = !this.isDirectoryExpanded;
        },
        goRoute(path) {
            if (path !== this.$route.path) {
                this.$router.push(path);
                window.scrollTo(0, 0);
            }
        },
        openLink(link) {
            if (link) {
                window.open(link, "_blank");
            }
        },
        downloadBrochure() {
            const anchor = document.createElement("a");
            anchor.href = this.brochureFile;
            anchor.download = "参展手册.pdf";
            anchor.style.display = "none";
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
        },
        showComingSoonDialog() {
            this.$alert(
                this.$t("exhibitor.comingSoon"),
                this.$t("exhibitor.tipTitle"),
                {
                    confirmButtonText: this.$t("exhibitor.confirm"),
                    showClose: false,
                }
            );
        },
        formatDate(value) {
            return value ? value.replace(/-/g, "/") : "";
        },
        handleAction(card) {
            if (card.type === "register") {
                this.register(card.value);
                return;
            }
            if (card.type === "route") {
                this.goRoute(card.value);
                return;
            }
            if (card.type === "download") {
                this.downloadBrochure();
                return;
            }
            if (card.type === "live") {
                this.showComingSoonDialog();
            }
        },
        openGalleryMore() {
            this.goRoute("/about");
        },
    },
};
</script>

<style scoped lang="less">
.home {
    width: 100%;
    min-width: 0;
}

.main {
    background:
        linear-gradient(120deg, rgba(219, 225, 235, 0.32) 0, rgba(219, 225, 235, 0.32) 1px, transparent 1px, transparent 60px),
        linear-gradient(145deg, #eff2f6 0%, #ffffff 42%, #f3f5f8 100%);
    background-size: 92px 92px, auto;
}

.section-shell {
    width: 88%;
    margin: 0 auto;
}

.section-title {
    font-size: 36px;
    line-height: 1.2;
    font-weight: 700;
    color: #333333;
    text-align: center;
}

.section-title-light {
    color: #ffffff;
}

.hero-section {
    width: 100%;
    line-height: 0;
}

.hero-image {
    width: 100%;
    display: block;
}

.intro-wrap {
    background-repeat: no-repeat;
    background-position: center top;
    background-size: cover;
    padding: 50px 0 54px;
}

.action-section {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 16px;
}

.action-card {
    min-height: 110px;
    height: 140px; padding: 20px 0;box-sizing:border-box;
    border-radius: 10px;
    background: linear-gradient(180deg, #5a3198 0%, #2843a5 100%);
    color: #ffffff;
    text-align: center;
    cursor: pointer;
    box-shadow: 0 14px 28px rgba(44, 56, 139, 0.18);
    display:flex; flex-direction:row; flex-wrap:wrap; justify-content:center; overflow:hidden; clear:both;align-items:center;
}

.action-title {box-sizing:border-box;
    width: 100%;
    display: block;
    font-size: 30px;
    line-height: 1.2;
    font-weight: 500;
    margin: 0 ;padding: 0;
}

.action-button {
    width: fit-content;box-sizing:border-box;
    min-width: 120px;
    max-width: 100%;
    margin: 0px auto 0;
    padding: 5px 16px;
    border: 1px solid rgba(255, 255, 255, 0.82);
    font-size: 16px;
    line-height: 1;
    border-radius: 5px;
}

.content-section {
    padding: 72px 0 0;
}

.intro-panel,
.news-panel {
    margin-top: 34px;
    display: grid;
    grid-template-columns: minmax(0, 1.18fr) minmax(520px, 0.82fr);
    gap: 54px;
    align-items: center;
}

.intro-copy {
    min-height: 252px;
    padding: 42px 48px;
    border: 1px solid #e4e8ef;
    background: rgba(255, 255, 255, 0.5);
    font-size: 24px;
    line-height: 50px;
    color: #333333;
    text-align: justify;
    text-indent: 2em;
}

.intro-copy.en {
    text-indent: 0;
}

.section-title,
.action-title,
.stat-label,
.stat-unit,
.news-text,
.contact-line,
.section-note,
.directory-more-text,
.action-button {
    overflow-wrap: anywhere;
}

.intro-media,
.news-cover,
.gallery-item {
    position: relative;
    overflow: hidden;
}

.intro-media,
.news-cover {
    cursor: pointer;
}

.intro-video,
.news-cover img,
.gallery-item img,
.logo-card img {
    width: 100%;
    display: block;
}

.intro-media {
    border: 1px solid rgba(29, 50, 116, 0.08);
}

.intro-video {
    min-height: 252px;
    object-fit: cover;
}

.data-section,
.gallery-section,
.contact-strip {
    background-repeat: no-repeat;
    background-position: center center;
    background-size: cover;
}

.data-section {
    padding: 32px 0 36px;
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin-top: 30px;
}

.stat-card {
    min-height: 164px;
    padding: 28px 26px 24px;
    align-items: baseline;
    gap: 14px;
    background: rgba(31, 53, 145, 0.2);
    border-right: 1px solid rgba(255, 255, 255, 0.18);
    display:flex; flex-direction:row; flex-wrap:no-wrap; justify-content:center; overflow:hidden; clear:both;align-items:center;
}

.stat-card:last-child {
    border-right: none;
}

.stat-value {
    font-size: 80px;
    line-height: 1;
    font-weight: 700;
    font-style: italic;
    color: #2ce1ff;
}

.stat-meta {
    color: #ffffff;
}

.stat-label {
    font-size: 26px;
    line-height: 1.35;
    font-weight: 600;
}

.stat-unit {
    margin-top: 8px;
    font-size: 20px;
}

.news-section {
    padding: 58px 0 70px;
}

.news-list {
    display: flex;
    flex-direction: column;
    gap: 22px;
}

.news-item {
    display: grid;
    grid-template-columns: 180px minmax(0, 1fr);
    cursor: pointer;
    color: #333333;
}

.news-item:hover .news-date,
.news-item:hover .news-text {
    color: #184ef6;
}

.news-date {
    color: #333;
    font-size: 24px;
    line-height: 1.65;
}

.news-text {
    font-size: 24px;
    line-height: 1.65;
}

.news-cover img {
    max-height: 380px;
    object-fit: cover;
}

.gallery-section {
    padding: 50px 0 56px;
    background-image:
        url("~@/assets/image/home-bg-3.png");
}

.gallery-marquee {
    margin-top: 45px;
    display: flex;
    gap: 18px;
    width: max-content;
    animation: gallery-scroll 48s linear infinite;
}

.gallery-section .section-shell {
    overflow: hidden;
}

.gallery-item {
    width: 320px;
    flex: 0 0 auto;
}

.gallery-item img {
    height: 225px;
    object-fit: cover;
}

.gallery-marquee:hover {
    animation-play-state: paused;
}

@keyframes gallery-scroll {
    from {
        transform: translateX(0);
    }
    to {
        transform: translateX(calc(-50% - 9px));
    }
}

.section-cta {
    width: 230px;
    max-width: 100%;
    margin: 42px auto 0;
    padding: 14px 0;
    border: 1px solid rgba(255, 255, 255, 0.82);
    border-radius: 999px;
    color: #ffffff;
    font-size: 18px;
    line-height: 1;
    text-align: center;
    cursor: pointer;
}

.directory-section {
    padding: 62px 0 42px;
    background: #fff;
}

.section-note {
    margin-top: 12px;
    text-align: center;
    color: #666666;
    font-size: 20px;
}

.logo-grid {
    margin-top: 38px;
    display: grid;
    grid-template-columns: repeat(8, minmax(0, 1fr));
    gap: 24px 28px;
}

.logo-card {
    min-height: 72px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.logo-card img {
    max-width: 100%;
    max-height: 84px;
    object-fit: contain;
}

.directory-more {
    margin: 26px auto 0;
    width: fit-content;
    color: #8f8f8f;
    font-size: 18px;
    text-align: center;
    cursor: pointer;
}

.directory-more-icon {
    position: relative;
    width: 20px;
    height: 18px;
    margin: 10px auto 0;
}

.directory-more-icon::before,
.directory-more-icon::after {
    content: "";
    position: absolute;
    left: 50%;
    width: 12px;
    height: 12px;
    border-right: 1px solid #b0b0b0;
    border-bottom: 1px solid #b0b0b0;
    transform: translateX(-50%) rotate(45deg);
}

.directory-more-icon::before {
    top: -2px;
}

.directory-more-icon::after {
    top: 4px;
}

.contact-strip {
    padding: 36px 0 42px;
    background-image:
        url("~@/assets/image/home-bg-4.png");
}

.contact-shell {
display:flex; flex-direction:row; flex-wrap:no-wrap; justify-content:center;align-items:center; overflow:hidden; clear:both;
    gap: 140px;
    align-items: center;
    color: #ffffff;
}

.contact-qr {
    text-align: center;
}

.contact-qr img {
    width: 180px;
    height: 180px;
    background: #ffffff;
}

.contact-caption {
    margin-top: 12px;
    font-size: 16px;
    color: #ffffff;
}

.contact-info {
    font-size: 16px;
    line-height: 2.2;
}

.contact-address {
    text-align: left;
}

.contact-line {
    line-height: 60px;
}

::v-deep(.home-header .info) {
    height: 70px;
    align-items: center;
    justify-content: center;
    padding: 0 46px;
    background: #47237a;
    position: relative;
}

::v-deep(.home-header .sign) {
    height: 64px !important;
}

::v-deep(.home-header .logo),
::v-deep(.home-header .login),
::v-deep(.home-header .info > .select) {
    display: none;
}

::v-deep(.home-header .nav) {
    padding-left: 0;
    gap: 84px;
}

::v-deep(.home-header .nav .item-info) {
    padding: 0;
    line-height: 64px;
}

::v-deep(.home-header .nav .item-info .title) {
    font-size: 21px;
    color: #ffffff;
    font-weight: 500;
    letter-spacing: 0.5px;
}

::v-deep(.home-header .nav #active),
::v-deep(.home-header .nav .item-info:hover) {
    background: transparent;
}

::v-deep(.home-header .nav #active .title),
::v-deep(.home-header .nav .item-info:hover .title),
::v-deep(.home-header .nav #active .nav-arrow),
::v-deep(.home-header .nav) {
    color: #2ce1ff;
}
::v-deep(.item-info:hover .nav-arrow) {
    color: #184ef6;
}

::v-deep(.home-header .nav .nav-arrow) {
    color: rgba(255, 255, 255, 0.92);
    width: 9px;
    height: 9px;
    margin-left: 10px;
    margin-top: -6px;
    border-right-width: 2px;
    border-bottom-width: 2px;
}

::v-deep(.home-header .nav .select) {
    margin-top: 0;
    padding: 8px 0;
    background: rgba(52, 28, 95, 0.96);
    box-shadow: 0 10px 24px rgba(21, 11, 43, 0.24);
}

::v-deep(.home-header .nav .select .select-item) {
    color: #ffffff;
    padding: 12px 20px 12px 16px;
}

::v-deep(.home-header .nav .select .select-item:hover) {
    background: rgba(44, 225, 255, 0.16);
    color: #2ce1ff;
}

::v-deep(.home-header .language) {
    height: 26px;
    margin-left: 54px;
    padding-left: 20px;
    line-height: 26px;
}

::v-deep(.home-header .language .sign),
::v-deep(.home-header .language .text) {
    color: #ffffff;
    font-size: 21px;
    font-weight: 500;
    letter-spacing: 1px;
}

::v-deep(.home-header .language #langactive) {
    color: #2ce1ff;
}

@media (max-width: 1366px) {
    .section-shell {
        width: 90%;
    }

    .section-title {
        font-size: 32px;
    }

    .intro-wrap {
        padding: 42px 0 48px;
    }

    .content-section {
        padding: 60px 0 0;
    }

    .action-section {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 14px;
    }

    .action-card {
        height: auto;
        min-height: 132px;
        padding: 18px 14px;
    }

    .action-title {
        font-size: 24px;
    }

    .action-button {
        min-width: 0;
        font-size: 15px;
    }

    .intro-panel,
    .news-panel {
        gap: 36px;
    }

    .intro-copy {
        padding: 34px 36px;
        font-size: 21px;
        line-height: 1.9;
    }

    .stat-card {
        min-height: 150px;
        padding: 24px 20px;
    }

    .stat-value {
        font-size: 62px;
    }

    .stat-label {
        font-size: 22px;
    }

    .stat-unit {
        font-size: 18px;
    }

    .news-date,
    .news-text {
        font-size: 21px;
        line-height: 1.55;
    }

    .gallery-marquee {
        gap: 16px;
    }

    .gallery-item img {
        height: 210px;
    }

    .section-note {
        font-size: 18px;
    }

    .logo-grid {
        gap: 20px 22px;
    }

    .contact-shell {
        gap: 40px;
        grid-template-columns: 180px minmax(0, 1fr) minmax(320px, 1fr);
    }

    .contact-line {
        line-height: 1.8;
    }

    ::v-deep(.home-header .info) {
        padding: 0 28px;
    }

    ::v-deep(.home-header .nav) {
        gap: 48px;
    }

    ::v-deep(.home-header .nav .item-info .title) {
        font-size: 18px;
    }

    ::v-deep(.home-header .language) {
        margin-left: 32px;
        padding-left: 18px;
    }

    ::v-deep(.home-header .language .sign),
    ::v-deep(.home-header .language .text) {
        font-size: 18px;
    }
}

@media (max-width: 1200px) {
    .section-shell {
        width: 92%;
    }

    .section-title {
        font-size: 30px;
    }

    .intro-wrap {
        padding: 38px 0 44px;
    }

    .content-section,
    .news-section {
        padding-top: 52px;
    }

    .action-section {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .intro-panel,
    .news-panel {
        grid-template-columns: 1fr;
        gap: 24px;
    }

    .intro-copy {
        min-height: 0;
        padding: 28px 30px;
        font-size: 19px;
        line-height: 1.8;
    }

    .intro-video {
        min-height: 0;
    }

    .stats-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .stat-card {
        border-right: none;
        border-bottom: 1px solid rgba(255, 255, 255, 0.18);
        min-height: 140px;
    }

    .stat-card:nth-last-child(-n + 2) {
        border-bottom: none;
    }

    .news-item {
        grid-template-columns: 150px minmax(0, 1fr);
    }

    .news-date,
    .news-text {
        font-size: 19px;
        line-height: 1.5;
    }

    .logo-grid {
        grid-template-columns: repeat(5, minmax(0, 1fr));
    }

    .contact-shell {
        grid-template-columns: 1fr;
        gap: 24px;
        text-align: center;
    }

    .contact-address {
        text-align: center;
    }

    .contact-line {
        line-height: 1.8;
    }

    ::v-deep(.home-header .info) {
        padding: 0 20px;
    }

    ::v-deep(.home-header .nav) {
        gap: 24px;
    }

    ::v-deep(.home-header .nav .item-info) {
        line-height: 56px;
    }

    ::v-deep(.home-header .nav .item-info .title) {
        font-size: 16px;
    }

    ::v-deep(.home-header .language) {
        position: static;
        margin-left: 0;
        padding-left: 0;
        line-height: 56px;
    }

    ::v-deep(.home-header .language .sign),
    ::v-deep(.home-header .language .text) {
        font-size: 16px;
    }
}

@media (max-width: 992px) {
    .section-title {
        font-size: 28px;
    }

    .content-section,
    .news-section {
        padding-top: 46px;
    }

    .action-section {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .action-card {
        min-height: 124px;
    }

    .action-title {
        font-size: 22px;
    }

    .intro-copy {
        padding: 24px 24px;
        font-size: 18px;
        line-height: 1.75;
    }

    .stat-card {
        gap: 10px;
    }

    .stat-value {
        font-size: 54px;
    }

    .stat-label {
        font-size: 20px;
    }

    .stat-unit {
        font-size: 17px;
    }

    .news-item {
        grid-template-columns: 1fr;
        gap: 4px;
    }

    .logo-grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    ::v-deep(.home-header .info) {
        height: auto;
        padding: 12px 18px;
        flex-wrap: wrap;
        row-gap: 10px;
    }

    ::v-deep(.home-header .sign) {
        height: auto !important;
    }

    ::v-deep(.home-header .nav) {
        width: 100%;
        justify-content: center;
        flex-wrap: wrap;
        gap: 12px 20px;
    }

    ::v-deep(.home-header .nav .item-info) {
        line-height: 1.4;
    }

    ::v-deep(.home-header .language) {
        width: 100%;
        justify-content: center;
        line-height: 1.4;
    }
}

@media (max-width: 768px) {
    .section-shell {
        width: 94%;
    }

    .section-title {
        font-size: 24px;
    }

    .intro-wrap {
        padding: 32px 0 36px;
    }

    .content-section,
    .news-section,
    .gallery-section,
    .directory-section,
    .contact-strip {
        padding-top: 36px;
    }

    .action-section,
    .stats-grid {
        grid-template-columns: 1fr;
    }

    .action-card {
        min-height: 0;
        padding: 16px 14px;
    }

    .action-title {
        font-size: 20px;
    }

    .action-button {
        width: 100%;
        padding-left: 12px;
        padding-right: 12px;
    }

    .intro-copy {
        padding: 22px 20px;
        font-size: 17px;
        line-height: 1.75;
    }

    .stat-card {
        min-height: 0;
        padding: 22px 18px;
        gap: 12px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.18);
    }

    .stat-card:nth-last-child(-n + 2) {
        border-bottom: 1px solid rgba(255, 255, 255, 0.18);
    }

    .stat-card:last-child {
        border-bottom: none;
    }

    .stat-value {
        font-size: 46px;
    }

    .news-item {
        grid-template-columns: 1fr;
        gap: 6px;
    }

    .news-date,
    .news-text {
        font-size: 18px;
    }

    .gallery-item img {
        height: 190px;
    }

    .logo-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 18px 16px;
    }

    .contact-shell {
        grid-template-columns: 1fr;
        gap: 18px;
        text-align: center;
    }

    .contact-address {
        text-align: center;
    }

    .contact-info {
        font-size: 15px;
        line-height: 1.8;
    }

    .contact-line {
        line-height: 1.7;
    }

    ::v-deep(.home-header .info) {
        padding: 10px 14px;
    }

    ::v-deep(.home-header .nav) {
        gap: 10px 16px;
    }

    ::v-deep(.home-header .nav .item-info .title) {
        font-size: 14px;
    }

    ::v-deep(.home-header .language .sign),
    ::v-deep(.home-header .language .text) {
        font-size: 14px;
    }
}

@media (max-width: 576px) {
    .section-title {
        font-size: 22px;
    }

    .intro-copy {
        padding: 20px 18px;
        font-size: 16px;
    }

    .news-date,
    .news-text {
        font-size: 16px;
    }

    .gallery-item img {
        height: 170px;
    }

    .section-cta {
        width: 100%;
        margin-top: 32px;
    }

    .section-note,
    .directory-more {
        font-size: 16px;
    }

    .logo-grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    .contact-qr img {
        width: 140px;
        height: 140px;
    }

    .contact-info,
    .contact-caption {
        font-size: 14px;
    }

    ::v-deep(.home-header .nav) {
        gap: 8px 12px;
    }

    ::v-deep(.home-header .nav .item-info .title) {
        font-size: 13px;
    }

    ::v-deep(.home-header .language .sign),
    ::v-deep(.home-header .language .text) {
        font-size: 13px;
    }
}
</style>

<style lang="less">
#app.home-responsive-layout {
    min-width: 0;
    width: 100%;
}
</style>
